import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to manage the PWA install prompt (beforeinstallprompt event).
 * The event is captured early in main.jsx on window.__pwaInstallPrompt so it
 * is never missed even if it fires before this hook mounts.
 *
 * After calling promptInstall(), the consumed event is cleared immediately.
 * Chrome may re-fire beforeinstallprompt (e.g. after user dismisses the native
 * dialog) — main.jsx picks it up and dispatches 'pwaInstallPromptReady' so the
 * hook refreshes with the new event.
 */
export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(() => window.__pwaInstallPrompt ?? null);
  const [isInstalled, setIsInstalled] = useState(() =>
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );

  useEffect(() => {
    if (isInstalled) return;

    const handlePromptReady = () => {
      if (window.__pwaInstallPrompt) {
        setDeferredPrompt(window.__pwaInstallPrompt);
      }
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      window.__pwaInstallPrompt = null;
    };

    window.addEventListener('pwaInstallPromptReady', handlePromptReady);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('pwaInstallPromptReady', handlePromptReady);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled]);

  const promptInstall = useCallback(async () => {
    const prompt = deferredPrompt ?? window.__pwaInstallPrompt;
    if (!prompt) return;
    // Clear old event immediately — it can only be used once.
    // Chrome will re-fire beforeinstallprompt if the user dismisses the native dialog.
    setDeferredPrompt(null);
    window.__pwaInstallPrompt = null;
    try {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
    } catch {
      // Prompt already consumed or unavailable — ignore
    }
  }, [deferredPrompt]);

  return { isInstalled, promptInstall, hasPrompt: !!deferredPrompt };
}
